import { useState } from 'react'

import type { KnowledgeItem } from '../types'
import { Icon } from './Icon'

type KnowledgeCanvasProps = {
  items: KnowledgeItem[]
  selectedId: string
  onSelect: (id: string) => void
}

const nodeGlyphs: Record<string, string> = {
  calculation: '运',
  equations: '方',
  functions: 'ƒ(x)',
  sets: '∩',
}

export function KnowledgeCanvas({
  items,
  selectedId,
  onSelect,
}: KnowledgeCanvasProps) {
  const [viewMode, setViewMode] = useState<'network' | 'list'>('network')
  const selected = items.find((item) => item.id === selectedId)

  return (
    <section aria-labelledby="knowledge-network-heading" className="knowledge-canvas panel-frame">
      <header className="panel-header knowledge-canvas__header">
        <div className="panel-title">
          <span className="panel-title__icon"><Icon name="network" /></span>
          <h2 id="knowledge-network-heading">知识网络</h2>
          <span className="information-mark" title="点击模块查看知识详情">i</span>
        </div>
        <div className="canvas-mode" aria-label="知识库视图">
          <button
            aria-pressed={viewMode === 'network'}
            className={viewMode === 'network' ? 'canvas-mode__active' : ''}
            onClick={() => setViewMode('network')}
            type="button"
          ><Icon name="network" size={14} />网络视图</button>
          <button
            aria-pressed={viewMode === 'list'}
            className={viewMode === 'list' ? 'canvas-mode__active' : ''}
            onClick={() => setViewMode('list')}
            type="button"
          ><Icon name="list" size={14} />列表视图</button>
        </div>
      </header>

      {viewMode === 'network' ? (
        <div className="knowledge-stage">
          <img
            alt="由四个数学模块连接到高一数学核心的抽象知识网络"
            className="knowledge-stage__background"
            height="1000"
            src="/knowledge-network.webp"
            width="1600"
          />
          <div aria-hidden="true" className="knowledge-core">
            <span><Icon name="bookOpen" size={34} /></span>
            <strong>高一数学</strong>
            <small>人教 A 版</small>
          </div>

          {items.map((item) => (
            <button
              aria-pressed={item.id === selectedId}
              className={`knowledge-node knowledge-node--${item.accent} knowledge-node--${item.id}`}
              key={item.id}
              onClick={() => onSelect(item.id === selectedId ? '' : item.id)}
              type="button"
            >
              <span className="knowledge-node__glyph" aria-hidden="true">
                {nodeGlyphs[item.id] ?? '知'}
              </span>
              <span className="knowledge-node__copy">
                <strong>{item.name}</strong>
                <small>{item.mastery}%</small>
              </span>
            </button>
          ))}

          {selected ? (
            <div className="knowledge-detail" aria-live="polite">
              <div>
                <span className={`knowledge-detail__signal knowledge-detail__signal--${selected.accent}`} />
                <div>
                  <strong>{selected.name}</strong>
                  <span>{selected.notePath}</span>
                </div>
              </div>
              <dl>
                <div><dt>成熟度</dt><dd>{selected.reviewLevel}</dd></div>
                <div><dt>前置</dt><dd>{selected.prerequisiteCount}</dd></div>
                <div><dt>关联</dt><dd>{selected.relatedCount}</dd></div>
                <div><dt>高考重要度</dt><dd>{selected.examImportance}</dd></div>
              </dl>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="knowledge-list-view">
          {items.map((item) => (
            <button key={item.id} onClick={() => onSelect(item.id)} type="button">
              <span className={`knowledge-detail__signal knowledge-detail__signal--${item.accent}`} />
              <span><strong>{item.name}</strong><small>{item.notePath}</small></span>
              <b>{item.reviewLevel}</b>
              <em>{item.auditStatus}</em>
            </button>
          ))}
        </div>
      )}

      <footer className="knowledge-legend">
        <span><i className="legend-dot legend-dot--muted" />未诊断</span>
        <span><i className="legend-dot legend-dot--blue" />学习中</span>
        <span><i className="legend-dot legend-dot--green" />已掌握</span>
        <span><i className="legend-dot legend-dot--amber" />待巩固</span>
        <span><i className="legend-dot legend-dot--outline" />未测评</span>
      </footer>
      <p className="knowledge-hint">提示：点击模块可查看知识点详情；切换列表视图可核对来源和审核状态。</p>
    </section>
  )
}
